import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import { User } from '../../types/supabase';
import { defaultPermissions, Permissions } from './permissionTypes';
import { permissionKeys } from './permissionKeys';
import { authKeys } from './authKeys';

/**
 * Hook for fetching user permissions with caching
 * This is the central hook for permission data - all permission checks
 * should use this hook to prevent duplicate API calls
 */
export function usePermissions() {
  const { handleError } = useQueryErrorHandler();
  const queryClient = useQueryClient();
  const user = queryClient.getQueryData<User | null>(authKeys.user());

  const {
    data: permissions,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: permissionKeys.user(user?.id),
    queryFn: async (): Promise<Permissions> => {
      try {
        // If no user, return default permissions
        if (!user) {
          logger.fetch('No user available, returning default permissions');
          return defaultPermissions;
        }

        logger.fetch('Fetching permissions for user', {
          userId: user.id,
          timestamp: new Date().toISOString(),
        });

        // Get the current session
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          logger.error('No active session when fetching permissions', {
            userId: user.id,
            timestamp: new Date().toISOString(),
          });

          return defaultPermissions;
        }

        // Call the secure Edge function to get permissions
        const { data, error } = await supabase.functions.invoke('get-user-permissions');

        if (error) {
          logger.error('Error from Edge function get-user-permissions', {
            error: error.message,
            status: error.status,
            userId: user.id,
          });
          throw error;
        }

        // Log the raw response for debugging
        logger.fetch('User permissions', {
          userId: user.id,
          isVIP: data.isVIP,
          tier: data.subscriptionTier,
          hasPermissions: !!data.permissions,
          responseTimestamp: new Date().toISOString(),
        });

        // Convert API response to Permissions interface
        const newPermissions: Permissions = {
          isVIP: !!data.isVIP,
          subscriptionTier: data.subscriptionTier || 'free',
          subscriptionStatus: data.subscriptionStatus || 'inactive',
          maxProjects: data.permissions.maxProjects || 3,
          maxCollaborators: data.permissions.maxCollaborators || 0,
          canAccessAllProjects: !!data.permissions.canAccessAllProjects,
          canUseAI: !!data.permissions.canUseAI,
          canCreateCustomColumns: !!data.permissions.canCreateCustomColumns,
        };

        return newPermissions;
      } catch (error) {
        const appError = handleError(error, {
          action: 'silent',
          context: { operation: 'fetchPermissions' },
        });

        logger.error('Failed to fetch permissions', {
          userId: user?.id,
          error: appError instanceof Error ? appError.message : String(appError),
          timestamp: new Date().toISOString(),
          stack: error instanceof Error ? error.stack : undefined,
        });

        return defaultPermissions;
      }
    },
    enabled: !!user,
    // Cache permissions for 5 minutes to match previous implementation
    staleTime: 5 * 60 * 1000,
    // Only refetch permissions when explicitly triggered or on component mount
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  /**
   * Mutation to force refresh permissions
   */
  const refreshPermissionsMutation = useMutation({
    mutationFn: async () => {
      return refetch();
    },
  });

  return {
    permissions: permissions || defaultPermissions,
    isVIP: permissions?.isVIP || false,
    subscriptionTier: permissions?.subscriptionTier || 'free',
    subscriptionStatus: permissions?.subscriptionStatus || 'inactive',
    maxProjects: permissions?.maxProjects || 3,
    maxCollaborators: permissions?.maxCollaborators || 0,
    canAccessAllProjects: permissions?.canAccessAllProjects || false,
    canUseAI: permissions?.canUseAI || false,
    canCreateCustomColumns: permissions?.canCreateCustomColumns || false,
    loading: isLoading,
    error,
    refetch: refreshPermissionsMutation.mutate,
  };
}

/**
 * Hook for checking if the current user is a VIP
 * Simplified wrapper around usePermissions
 */
export function useVIPStatus(): {
  isVIP: boolean;
  loading: boolean;
} {
  const { isVIP, loading } = usePermissions();

  return {
    isVIP,
    loading,
  };
}
